import { Injectable, signal, effect } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ActualTheme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private document = inject(DOCUMENT);
  
  // Signals for reactive theme management
  private themeMode = signal<ThemeMode>('system');
  private actualTheme = signal<ActualTheme>('light');
  
  // Public readonly signals
  readonly currentThemeMode = this.themeMode.asReadonly();
  readonly currentActualTheme = this.actualTheme.asReadonly();
  
  // CSS transition for smooth theme changes
  private readonly THEME_TRANSITION = 'background-color 250ms ease, color 150ms ease, border-color 250ms ease';
  
  constructor() {
    // Load saved theme preference
    this.loadSavedTheme();
    
    // Listen for system theme changes
    this.setupSystemThemeListener();
    
    // Apply theme when actual theme changes
    effect(() => {
      this.applyTheme(this.actualTheme());
    });
  }
  
  /**
   * Toggle between light and dark themes
   */
  toggleTheme(): void {
    const newTheme = this.actualTheme() === 'light' ? 'dark' : 'light';
    this.themeMode.set(newTheme);
    this.actualTheme.set(newTheme);
    this.saveThemePreference(newTheme);
  }
  
  /**
   * Set specific theme mode
   */
  setThemeMode(mode: ThemeMode): void {
    this.themeMode.set(mode);
    
    if (mode === 'system') {
      this.detectSystemTheme();
    } else {
      this.actualTheme.set(mode as ActualTheme);
    }
    
    this.saveThemePreference(mode);
  }
  
  /**
   * Load theme from localStorage
   */
  private loadSavedTheme(): void {
    const savedTheme = localStorage.getItem('farturk-theme') as ThemeMode;
    
    if (savedTheme) {
      this.themeMode.set(savedTheme);
      
      if (savedTheme === 'system') {
        this.detectSystemTheme();
      } else {
        this.actualTheme.set(savedTheme as ActualTheme);
      }
    } else {
      // Default to system theme
      this.detectSystemTheme();
    }
  }
  
  /**
   * Save theme preference to localStorage
   */
  private saveThemePreference(theme: ThemeMode): void {
    localStorage.setItem('farturk-theme', theme);
  }
  
  /**
   * Detect system color scheme preference
   */
  private detectSystemTheme(): void {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.actualTheme.set(prefersDark ? 'dark' : 'light');
  }
  
  /**
   * Listen for system theme changes
   */
  private setupSystemThemeListener(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    mediaQuery.addEventListener('change', (e) => {
      if (this.themeMode() === 'system') {
        this.actualTheme.set(e.matches ? 'dark' : 'light');
      }
    });
  }
  
  /**
   * Apply theme to document
   */
  private applyTheme(theme: ActualTheme): void {
    const root = this.document.documentElement;

    // Enable smooth transitions
    this.enableTransitions();

    // Set theme attribute (compatible with SCSS [data-theme] selectors)
    root.setAttribute('data-theme', theme);

    // Update meta theme-color for mobile browsers
    this.updateMetaThemeColor(theme);

    // Load dynamic theme CSS if needed
    this.loadThemeStyles(theme);
  }
  
  /**
   * Enable smooth transitions for theme changes
   */
  private enableTransitions(): void {
    const root = this.document.documentElement;
    root.style.setProperty('--theme-transition', this.THEME_TRANSITION);
    
    // Apply transition to all elements temporarily
    const style = this.document.createElement('style');
    style.textContent = `
      * {
        transition: var(--theme-transition) !important;
      }
    `;
    this.document.head.appendChild(style);
    
    // Remove transition override after animation
    setTimeout(() => {
      style.remove();
    }, 300);
  }
  
  /**
   * Update meta theme-color for mobile browsers
   */
  private updateMetaThemeColor(theme: ActualTheme): void {
    let metaThemeColor = this.document.querySelector('meta[name="theme-color"]');
    
    if (!metaThemeColor) {
      metaThemeColor = this.document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      this.document.head.appendChild(metaThemeColor);
    }
    
    // FARTURK brand colors
    const themeColors = {
      light: '#7c3aed', // FARTURK purple
      dark: '#5b21b6'   // Darker purple for dark theme
    };
    
    metaThemeColor.setAttribute('content', themeColors[theme]);
  }
  
  /**
   * Load dynamic theme styles (for future use with separate CSS files)
   */
  private loadThemeStyles(theme: ActualTheme): void {
    // Remove existing theme link if any
    const existingThemeLink = this.document.getElementById('theme-styles');
    if (existingThemeLink) {
      existingThemeLink.remove();
    }
    
    // This will be used when we have separate theme CSS files
    // For now, we're using class-based theming
    /*
    const link = this.document.createElement('link');
    link.id = 'theme-styles';
    link.rel = 'stylesheet';
    link.href = `assets/themes/farturk-${theme}.css`;
    this.document.head.appendChild(link);
    */
  }
  
  /**
   * Get computed color value from CSS variable
   */
  getComputedColor(cssVariable: string): string {
    const styles = getComputedStyle(this.document.documentElement);
    return styles.getPropertyValue(cssVariable).trim();
  }
  
  /**
   * Set custom CSS variable
   */
  setCustomProperty(property: string, value: string): void {
    this.document.documentElement.style.setProperty(property, value);
  }
}